import { useFragment } from "@apollo/client/react";
import { Dispatch, SetStateAction, useState, useMemo, JSX } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useTranslation } from "react-i18next";

import { IndicatorNumber, DataTable } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Shift, TenderDurationFragment, TenderDurationFragmentDoc } from "@/graphql/__generated__/graphql";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { SubmitAction } from "@/constants/enums";
import { getColumnsShifts } from "../columns";
import { ConfirmRemoveAlert } from "../Dialog";
import { TShiftSchema } from "../Models";
import AddShiftComponent from "./AddShiftComponent";
import EditShiftComponent from "./EditShiftComponent";

interface Props {
  shiftsToDisplay?: Shift[];
  onAddShift: (_shift: TShiftSchema, _action: SubmitAction, _setShowShiftsModal: Dispatch<SetStateAction<boolean>>) => Promise<void>;
  onRemoveShift: (_shift: Shift) => Promise<void>;
  showRemoveShiftAlert: boolean;
  setShowRemoveShiftAlert: Dispatch<SetStateAction<boolean>>;
  userCanEdit?: boolean;
  onEditShift: (_shift: TShiftSchema, _selectedShift: Shift, _setShowEditShiftModal: Dispatch<SetStateAction<boolean>>) => Promise<void>;
  isProcessing?: boolean;
}

/**
 * Shifts component. Component to display shifts and add/edit shifts.
 *
 * @param {Props} props
 *
 * @returns {JSX.Element}
 */
const ShiftsComponent = (props: Props): JSX.Element => {
  // Props
  const {
    shiftsToDisplay,
    onAddShift,
    onRemoveShift,
    userCanEdit,
    onEditShift,
    isProcessing = false,
  } = props;

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["translation", "components"]);
  const { tenderID, serviceID } = useParams();
  const { isNewService } = useOutletContext<EditTenderContext>();

  // States
  const [showRemoveShiftAlert, setShowRemoveShiftAlert] = useState<boolean>(false);
  const [showShiftsModal, setShowShiftsModal] = useState<boolean>(false);
  const [showEditShiftModal, setShowEditShiftModal] = useState<boolean>(false);
  const [currentShift, setCurrentShift] = useState<Shift | undefined>(undefined);

  // Get existing Tender contract duration from cache
  const { data: tenderDuration } = useFragment({
    fragment: TenderDurationFragmentDoc,
    from: {
      __typename: "Tender",
      id: tenderID,
    },
  }) as { data: TenderDurationFragment };

  // Sort shifts by startTime and endTime
  const sortedShifts = useMemo(() => {
    if (!shiftsToDisplay) return [];
    return [...shiftsToDisplay].sort((a, b) => {
      // First compare by startTime
      if (a.startTime !== b.startTime) {
        return a.startTime.localeCompare(b.startTime);
      }
      // If startTimes are equal, compare by endTime
      return a.endTime.localeCompare(b.endTime);
    });
  }, [shiftsToDisplay]);

  /**
   * OnClick handler for the remove button in the Shifts table
   * @param {Shift} shift - Current Shift object
   */
  const handleRemoveClick = (shift: Shift) => {
    setCurrentShift(shift);
    setShowRemoveShiftAlert(true);
  };

  /**
   * OnClick handler for the edit button in the Shifts table
   * @param {Shift} shift - Current Shift object
   */
  const handleEditClick = (shift: Shift) => {
    setShowEditShiftModal(true);
    setCurrentShift(shift);
  };

  return (
    <div>
      <div className="flex items-end justify-between my-4">
        <div className="flex items-center gap-2 h-[40px]">
          <h2 className="text-lg font-semibold">
            {t("shifts.title")}
          </h2>
          <IndicatorNumber
            number={(sortedShifts.length || 0)}
          />
        </div>
        {(userCanEdit || isNewService) && (
          <Button
            onClick={() => {
              setShowShiftsModal(true);
            }}
            variant={"outline"}
          >
            {t("shifts.btnaddshift")}
          </Button>
        )}
      </div>

      {/* Shifts Modal */}
      {showShiftsModal && (
        <AddShiftComponent
          onAddShift={onAddShift}
          serviceID={Number(serviceID)}
          showShiftsModal={showShiftsModal}
          setShowShiftsModal={setShowShiftsModal}
          tenderDuration={tenderDuration}
          selectedShift={currentShift}
          isProcessing={isProcessing}
        />
      )}

      {currentShift && showEditShiftModal && (
        <EditShiftComponent
          showEditShiftModal={showEditShiftModal}
          setShowEditShiftModal={setShowEditShiftModal}
          tenderDuration={tenderDuration}
          selectedShift={currentShift}
          isNewService={isNewService}
          onEditShift={onEditShift}
          isProcessing={isProcessing}
        />
      )}

      <div className="mt-4">
        <DataTable
          headerStyling="font-normal text-sm"
          disableRowColour={true}
          loading={false}
          displayAmount={sortedShifts.length}
          data={sortedShifts}
          columns={getColumnsShifts(intl, t, userCanEdit as boolean, handleRemoveClick, handleEditClick, tenderDuration, isProcessing)}
          className="max-h-120"
        />
      </div>
      {/* End of shifts */}

      {/* Remove Shift Alert Dialog */}
      <ConfirmRemoveAlert
        open={showRemoveShiftAlert}
        onOpenChange={setShowRemoveShiftAlert}
        actionButtonFn={() => { onRemoveShift(currentShift as Shift); setShowRemoveShiftAlert(false); }}
        bodyText={t("components:alertdialogs.removeshift")}
      />
    </div>
  );
};

export default ShiftsComponent;
